<template>
  <div class="tpp">
    <!-- Header row: file selector + platform + close -->
    <div class="tpp-header">
      <v-select
        v-model="selectedFile"
        :options="files"
        :get-option-label="fileLabel"
        placeholder="오디오 파일 선택..."
        class="tpp-file-select"
        @input="loadFile"
      />
      <v-select
        v-model="platform"
        :options="platformOptions"
        :reduce="o => o.value"
        :clearable="false"
        class="tpp-platform-select"
      />
      <button class="tpp-close-btn" @click="$emit('cancel')" title="닫기">✕</button>
    </div>

    <!-- Hidden audio element -->
    <audio
      ref="audio"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onMeta"
      @ended="isPlaying = false"
      @error="audioError = true"
    />

    <!-- Timeline -->
    <div
      ref="timeline"
      class="tpp-timeline"
      @mousedown="onTimelineClick"
      @touchstart.prevent="onTimelineClick"
    >
      <template v-if="duration > 0">
        <!-- Waveform bars -->
        <div class="tpp-wave">
          <div
            v-for="(h, i) in waveformBars"
            :key="i"
            class="tpp-wave-bar"
            :class="{ 'in-region': isBarInRegion(i) }"
            :style="{ height: h + '%' }"
          />
        </div>
        <!-- Selected region highlight (draggable) -->
        <div
          class="tpp-region"
          :class="{ 'is-dragging': dragging === 'region' }"
          :style="regionStyle"
          @mousedown.stop="beginRegionDrag"
          @touchstart.prevent.stop="beginRegionDrag"
        />
        <!-- Playhead -->
        <div class="tpp-playhead" :style="{ left: playPercent + '%' }" />
        <!-- Start handle -->
        <div
          class="tpp-handle tpp-handle--start"
          :style="{ left: startPercent + '%' }"
          @mousedown.stop="beginDrag('start', $event)"
          @touchstart.prevent.stop="beginDrag('start', $event)"
        >
          <div class="tpp-handle__grip" />
          <div class="tpp-handle__label above">{{ fmtTime(previewStart) }}</div>
        </div>
        <!-- End handle -->
        <div
          class="tpp-handle tpp-handle--end"
          :style="{ left: endPercent + '%' }"
          @mousedown.stop="beginDrag('end', $event)"
          @touchstart.prevent.stop="beginDrag('end', $event)"
        >
          <div class="tpp-handle__grip" />
          <div class="tpp-handle__label above">{{ fmtTime(previewEnd) }}</div>
        </div>
      </template>
      <!-- Empty / loading states -->
      <div v-else-if="!selectedFile" class="tpp-timeline__empty">
        파일을 선택하면 파형이 표시됩니다
      </div>
      <div v-else class="tpp-timeline__loading">
        <span class="tpp-spinner" />
        <span>로딩 중...</span>
      </div>
    </div>

    <!-- Controls bar -->
    <div class="tpp-controls" v-if="selectedFile">
      <!-- Play/Pause -->
      <button class="tpp-play-btn" :disabled="duration === 0" @click="togglePlay">
        <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <polygon points="5,3 19,12 5,21" />
        </svg>
        <svg v-else viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
        </svg>
      </button>
      <!-- Time -->
      <div class="tpp-time">
        <span class="tpp-time__current">{{ fmtTime(currentTime) }}</span>
        <span class="tpp-time__sep">/</span>
        <span class="tpp-time__total">{{ fmtTime(duration) }}</span>
      </div>
      <!-- Selection summary -->
      <div class="tpp-selection" v-if="duration > 0">
        <span :class="['tpp-platform-badge', platform]">{{ platform }}</span>
        <span class="tpp-sel-range">{{ fmtTime(previewStart) }} → {{ fmtTime(previewEnd) }}</span>
        <span class="tpp-sel-dur">{{ Math.max(0, Math.floor(previewEnd - previewStart)) }}s</span>
      </div>
      <!-- Confirm -->
      <vs-button
        color="success"
        type="gradient"
        class="tpp-confirm-btn"
        :disabled="duration === 0 || previewEnd <= previewStart"
        @click="confirm"
      >
        추가
      </vs-button>
    </div>
  </div>
</template>

<script>
const BAR_COUNT = 100

export default {
  name: 'TrackPreviewPicker',
  props: {
    files:           { type: Array,  default: () => [] },
    platformOptions: { type: Array,  default: () => [{ label: 'TikTok', value: 'tiktok' }] },
    initialPlatform: { type: String, default: 'tiktok' },
  },
  mounted() {
    if (this.files && this.files.length) {
      this.selectedFile = this.files[0]
      this.loadFile(this.files[0])
    }
  },
  data() {
    return {
      selectedFile:  null,
      platform:      this.initialPlatform,
      isPlaying:     false,
      currentTime:   0,
      duration:      0,
      previewStart:  0,
      previewEnd:    0,
      waveformBars:  [],
      dragging:      null,
      audioError:    false,
    }
  },
  computed: {
    startPercent() { return this.duration ? (this.previewStart / this.duration) * 100 : 0 },
    endPercent()   { return this.duration ? (this.previewEnd   / this.duration) * 100 : 0 },
    playPercent()  { return this.duration ? (this.currentTime  / this.duration) * 100 : 0 },
    regionStyle() {
      return {
        left:  this.startPercent + '%',
        width: Math.max(0, this.endPercent - this.startPercent) + '%',
      }
    },
  },
  methods: {
    fileLabel(f) {
      return f.display_name || f.name || f.original_name || 'Audio file'
    },
    loadFile(file) {
      if (!file) return
      const audio = this.$refs.audio
      audio.src = file.remote_path
      audio.load()
      this.isPlaying   = false
      this.currentTime = 0
      this.duration    = 0
      this.waveformBars = []
      this.audioError  = false
    },
    onMeta() {
      const d = this.$refs.audio.duration
      if (!isFinite(d) || d <= 0) return
      this.duration = d
      // Default region: first 30 s (or full track if shorter)
      const len = Math.min(30, d)
      this.previewStart = 0
      this.previewEnd   = len
      this.waveformBars = this.makeBars(d)
    },
    makeBars(duration) {
      return Array.from({ length: BAR_COUNT }, (_, i) => {
        const t = i / BAR_COUNT
        // multi-sine for a natural waveform silhouette
        const v = 15
          + Math.abs(Math.sin(t * 43 + duration * 0.31)) * 38
          + Math.abs(Math.sin(t * 17 + duration * 0.77)) * 22
          + Math.abs(Math.sin(t * 91 + duration * 1.13)) * 14
          + Math.abs(Math.sin(t *  7 + duration * 0.53)) *  8
        return Math.min(92, Math.max(8, v))
      })
    },
    isBarInRegion(i) {
      if (!this.duration) return false
      const t = (i / BAR_COUNT) * this.duration
      return t >= this.previewStart && t <= this.previewEnd
    },
    onTimeUpdate() {
      this.currentTime = this.$refs.audio.currentTime
    },
    togglePlay() {
      if (this.duration === 0) return
      const audio = this.$refs.audio
      if (this.isPlaying) {
        audio.pause()
        this.isPlaying = false
      } else {
        audio.play().catch(() => { this.isPlaying = false })
        this.isPlaying = true
      }
    },
    onTimelineClick(e) {
      if (!this.duration || this.dragging) return
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const rect    = this.$refs.timeline.getBoundingClientRect()
      const ratio   = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      this.$refs.audio.currentTime = ratio * this.duration
    },
    beginRegionDrag(e) {
      const startX    = e.touches ? e.touches[0].clientX : e.clientX
      const initStart = this.previewStart
      const initEnd   = this.previewEnd
      const len       = initEnd - initStart
      this.dragging   = 'region'

      const onMove = (ev) => {
        const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX
        const rect    = this.$refs.timeline.getBoundingClientRect()
        const deltaT  = ((clientX - startX) / rect.width) * this.duration
        let newStart  = initStart + deltaT
        let newEnd    = initEnd   + deltaT
        if (newStart < 0)              { newStart = 0;                    newEnd = len }
        if (newEnd   > this.duration)  { newEnd   = this.duration;        newStart = this.duration - len }
        this.previewStart = newStart
        this.previewEnd   = newEnd
      }
      const onUp = () => {
        this.dragging = null
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup',   onUp)
        document.removeEventListener('touchmove', onMove)
        document.removeEventListener('touchend',  onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup',   onUp)
      document.addEventListener('touchmove', onMove, { passive: false })
      document.addEventListener('touchend',  onUp)
    },
    beginDrag(handle, e) {
      this.dragging = handle
      const onMove = (ev) => {
        const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX
        const rect    = this.$refs.timeline.getBoundingClientRect()
        const ratio   = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
        const time    = ratio * this.duration

        if (handle === 'start') {
          this.previewStart = Math.min(time, this.previewEnd - 1)
        } else {
          this.previewEnd = Math.max(time, this.previewStart + 1)
        }
      }
      const onUp = () => {
        this.dragging = null
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup',   onUp)
        document.removeEventListener('touchmove', onMove)
        document.removeEventListener('touchend',  onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup',   onUp)
      document.addEventListener('touchmove', onMove, { passive: false })
      document.addEventListener('touchend',  onUp)
    },
    fmtTime(s) {
      const sec = Math.floor(s)
      return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`
    },
    confirm() {
      if (this.previewEnd <= this.previewStart) return
      this.$emit('confirm', {
        start_point: Math.floor(this.previewStart),
        end_point:   Math.floor(this.previewEnd),
        platform:    this.platform,
      })
    },
  },
  beforeDestroy() {
    const audio = this.$refs.audio
    if (audio) { audio.pause(); audio.src = '' }
  },
}
</script>

<style scoped>
/* ── Root ── */
.tpp {
  border: 1px solid rgba(65, 69, 97, 0.6);
  border-radius: 6px;
  overflow: hidden;
  background: #1a1f3c;
  margin-bottom: 12px;
}

/* ── Header ── */
.tpp-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(65, 69, 97, 0.5);
}
.tpp-file-select { flex: 1 1 0; min-width: 0; }
.tpp-platform-select { width: 130px; flex-shrink: 0; }
.tpp-close-btn {
  background: transparent;
  border: 1px solid transparent;
  color: #6b7094;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  flex-shrink: 0;
  padding: 0;
}
.tpp-close-btn:hover {
  background: rgba(234, 84, 85, 0.1);
  border-color: rgba(234, 84, 85, 0.3);
  color: #ea5455;
}

/* ── Timeline ── */
.tpp-timeline {
  position: relative;
  height: 72px;
  background: #12183a;
  cursor: crosshair;
  overflow: hidden;
}

/* Empty / loading */
.tpp-timeline__empty,
.tpp-timeline__loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7094;
  font-style: italic;
}

/* Spinner */
.tpp-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(107, 112, 148, 0.3);
  border-top-color: #7b84c4;
  border-radius: 50%;
  animation: tpp-spin 0.7s linear infinite;
}
@keyframes tpp-spin { to { transform: rotate(360deg); } }

/* ── Waveform bars ── */
.tpp-wave {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: 1px;
  padding: 6px 0;
  pointer-events: none;
}
.tpp-wave-bar {
  flex: 1;
  min-width: 1px;
  background: rgba(80, 90, 140, 0.55);
  border-radius: 1px;
  transition: background 0.08s;
}
.tpp-wave-bar.in-region {
  background: rgba(40, 199, 111, 0.75);
}

/* ── Region ── */
.tpp-region {
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(40, 199, 111, 0.08);
  border-left:  2px solid #28c76f;
  border-right: 2px solid #28c76f;
  pointer-events: all;
  cursor: grab;
  z-index: 2;
}
.tpp-region.is-dragging {
  cursor: grabbing;
  background: rgba(40, 199, 111, 0.14);
}

/* ── Playhead ── */
.tpp-playhead {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.55);
  pointer-events: none;
  transform: translateX(-0.5px);
}

/* ── Handles ── */
.tpp-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  z-index: 4;
  cursor: ew-resize;
}
.tpp-handle__grip {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -4px;
  width: 8px;
  background: transparent;
}
.tpp-handle__grip::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 3px;
  width: 2px;
  background: #28c76f;
  border-radius: 1px;
}
.tpp-handle__label {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #28c76f;
  white-space: nowrap;
  background: rgba(26, 31, 60, 0.9);
  padding: 1px 5px;
  border-radius: 3px;
  line-height: 1.6;
  pointer-events: none;
}
.tpp-handle__label.above { bottom: calc(100% + 3px); }

/* ── Controls ── */
.tpp-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-top: 1px solid rgba(65, 69, 97, 0.45);
  background: #161b38;
}
.tpp-play-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(65, 69, 97, 0.7);
  background: transparent;
  color: #c2c6dc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  padding: 0;
}
.tpp-play-btn:hover:not(:disabled) {
  background: rgba(40, 199, 111, 0.12);
  border-color: #28c76f;
  color: #28c76f;
}
.tpp-play-btn:disabled { opacity: 0.35; cursor: default; }
.tpp-time {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: #c2c6dc;
  white-space: nowrap;
}
.tpp-time__sep { color: #414561; }
.tpp-time__total { color: #6b7094; }

.tpp-selection {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 1;
  min-width: 0;
}
.tpp-platform-badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  padding: 2px 7px;
  border-radius: 3px;
  text-transform: uppercase;
  white-space: nowrap;
  line-height: 1.5;
}
.tpp-platform-badge.tiktok {
  background: rgba(238, 29, 82, 0.1);
  color: #ee1d52;
  border: 1px solid rgba(238, 29, 82, 0.22);
}
.tpp-sel-range {
  font-size: 12px;
  font-weight: 600;
  color: #c2c6dc;
  white-space: nowrap;
}
.tpp-sel-dur {
  font-size: 11px;
  color: #6b7094;
  white-space: nowrap;
}

.tpp-confirm-btn { flex-shrink: 0; }
</style>
